import { map } from "ramda";
import { type FC } from "react";
import { ActionButtonIntent, AlertDialog } from "widgets";
import { type ControlledDialog, useControlledDialog } from "widgets/hooks";

import { selMessage } from "@/messages/message.ts";
import { useDeleteProjectFilesMutation } from "@/modules/files/graphql/files-mutations.gql";
import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import { notify } from "@/modules/notifications/notify";

interface FilesDeletionDialogProps extends ControlledDialog {
  selectedFiles: BrowsingProjectFileFragment[];
  onDeleted?: () => void;
}

export const FilesDeletionControlledAlertDialog: FC<FilesDeletionDialogProps> = (props) => {
  const [deleteFiles, { loading, error }] = useDeleteProjectFilesMutation({
    variables: { fileIds: map((f) => f.id, props.selectedFiles) },
  });

  const { dialogRef, dialogProps, state, underlayProps } = useControlledDialog(props.onCancel);

  const confirmationMessage: string =
    "Are you sure you want to delete" +
    (props.selectedFiles.length === 1
      ? ` '${props.selectedFiles?.[0]?.fileName}'?`
      : ` these ${props.selectedFiles.length} files or folders?`);

  const deleteSelectedFiles = async () => {
    await deleteFiles().then(() =>
      notify.info({
        title: selMessage({
          id: "file.delete.successTitle",
          values: {
            fileCount: props.selectedFiles.length,
          },
        }),
        children: selMessage({
          id: "file.delete.successMessage",
          values: {
            fileCount: props.selectedFiles.length,
          },
        }),
      }),
    );
    props.onDeleted?.();
  };

  return state.isOpen ? (
    <AlertDialog
      ref={dialogRef}
      title={"File or Folder Deletion"}
      mainButton={{
        isLoading: loading,
        label: "Delete",
        intent: ActionButtonIntent.Danger,
        onPress: deleteSelectedFiles,
      }}
      errorCallout={error ?? undefined}
      domProps={dialogProps}
      closeFn={state.close}
      underlayProps={underlayProps}
    >
      <div>{confirmationMessage}</div>
      <div style={{ fontSize: 12, marginTop: 10 }}>
        {props.selectedFiles.length > 1 ? (
          <ul style={{ margin: 0 }}>
            {props.selectedFiles.slice(0, 5).map((file, i) => (
              <li key={`files-page-selected-files-${i}`} style={{ lineHeight: 1.15 }}>
                {file?.fileName}
              </li>
            ))}
            {props.selectedFiles.length > 5 ? <li>...</li> : null}
          </ul>
        ) : null}
      </div>
    </AlertDialog>
  ) : null;
};
